import { type ComponentProps } from 'react'
import { Field as BaseField } from '@base-ui/react/field'
import { twMerge } from 'tailwind-merge'
import { itemTitleClasses } from '../../item/styles'
import { useFieldContext } from './context'
import { FIELD_DISABLED_TEXT } from './styles'
import type { PartProps } from '../../../lib/part-props'

interface FieldLabelProps extends PartProps<ComponentProps<typeof BaseField.Label>> {
  className?: string
  /**
   * Hide the label from the eye only. It stays in the DOM and keeps naming the control, so a
   * search field in a toolbar can skip the visible label without going unnamed.
   */
  srOnly?: boolean
}

/**
 * A real `<label>`, associated with the control by Base UI, so clicking it focuses the field
 * without anyone passing an id.
 *
 * Pass `nativeLabel={false}` when the thing being labelled is button-like (`Select.Trigger`,
 * `Checkbox`). A native label hands its clicks and its hover to the control, which on a button
 * reads as the button lighting up whenever the pointer crosses the words.
 */
function FieldLabel({ className, srOnly, nativeLabel, render, children, ...props }: FieldLabelProps) {
  const { required } = useFieldContext()

  const asterisk = required ? (
    // Decoration over the attribute, never instead of it: the control carries `required`.
    <span aria-hidden="true" className="text-danger-subtle ml-[0.25em]">
      *
    </span>
  ) : null

  return (
    <BaseField.Label
      data-slot="field-label"
      data-sr-only={srOnly || undefined}
      nativeLabel={nativeLabel}
      // With `nativeLabel={false}` Base UI still renders a `<label>` and warns. A non-native label has
      // to be another element, or the browser forwards its clicks to the control.
      render={render ?? (nativeLabel === false ? <span /> : undefined)}
      className={twMerge(itemTitleClasses, FIELD_DISABLED_TEXT, 'w-fit', srOnly && 'sr-only', className)}
      {...props}
    >
      {children}
      {asterisk}
    </BaseField.Label>
  )
}

export { FieldLabel }
export type { FieldLabelProps }
